import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';
import usePurchase from 'hooks/store/usePurchase';
import usePayment from 'hooks/store/usePayment';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { selectCardCheckoutDecision } from 'selectors/cardCheckoutSelectors';

const cardSelector = formValueSelector('card');
const purchaserSelector = formValueSelector('purchaser');

/**
 * useQuickCheckoutState hook.
 * Compose existing store hooks and derive QuickCheckout-specific state.
 * Replaces mapStateToProps from QuickCheckoutContainer.
 * @returns {object} QuickCheckout state.
 */
function useQuickCheckoutState() {
  const purchase = usePurchase();
  const payment = usePayment();
  const env = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();

  const isUpdating = useSelector((state) => state.purchase.get('isUpdating'));
  const paymentEngine = useSelector((state) => state.purchase.get('paymentEngine'));
  const selectedPaymentMethod = useSelector((state) => state.purchase.get('selectedPaymentMethod'));
  const cardCheckoutMode = useSelector(
    (state) => selectCardCheckoutDecision(state, selectedPaymentMethod).mode,
  );

  const cardValues = useSelector((state) => {
    return cardSelector(
      state,
      'holderName',
      'cardNumber',
      'cvv2',
      'expirationMonth',
      'expirationYear',
      'expirationDate',
      'paymentPlan',
      'monthlySelectedPlan',
      'billingAddress',
      'cardBrand',
    );
  });

  const purchaserFormValues = useSelector((state) => {
    return purchaserSelector(
      state,
      'purchaserFirstName',
      'purchaserLastName',
      'purchaserSecondLastName',
      'email',
      'phone',
      'phoneCountry',
      'phoneCode',
      'documentType',
      'documentId',
    );
  });

  const fetchingPayment = payment.isFetching || false;
  const paymentId = payment.id || null;
  const cardErrorOccured = payment.cardErrorOccured || false;
  const insuranceConfig = purchase.config || {};

  const carbonOffset = purchase.config?.carbonOffset;
  const wantsCarbonOffset = Boolean(purchase.wantsCarbonOffset);
  const updatingCarbonOffset = Boolean(purchase.updatingCarbonOffset);

  const purchaser = useMemo(() => {
    if (purchaserFormValues?.purchaserFirstName) {
      const fields = { ...purchaserFormValues };
      if (fields.phone) fields.phone = fields.phone.replace(/ /g, '');
      return fields;
    }
    const firstPassenger = purchase.passengers?.[0] || {};
    return {
      purchaserFirstName: firstPassenger.firstName,
      purchaserLastName: firstPassenger.lastName,
      email: firstPassenger.email,
      phone: firstPassenger.phone,
      phoneCode: firstPassenger.phoneCode,
      documentId: firstPassenger.documentId,
      documentType: firstPassenger.documentType,
    };
  }, [purchaserFormValues, purchase.passengers]);

  return {
    purchase,
    payment,
    env,
    features,
    isUpdating,
    fetchingPayment,
    paymentId,
    cardErrorOccured,
    cardValues,
    cardCheckoutMode,
    paymentEngine,
    selectedPaymentMethod,
    purchaser,
    purchaserFormValues,
    insuranceMessage: insuranceConfig.insuranceMessage,
    insuranceAmount: insuranceConfig.insuranceAmount,
    insuranceAmountWithTaxes: insuranceConfig.insuranceAmountWithTaxes,
    insuranceAmountIsUnitPrice: insuranceConfig.unitAmount,
    carbonOffset,
    wantsCarbonOffset,
    updatingCarbonOffset,
  };
}

export default useQuickCheckoutState;
